import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "../../../components/Button";
import { Drawer } from "../../../components/Drawer";
import { Input } from "../../../components/Input";
import { UpdateKanbanDto } from "../../../model/kanban/dto/UpdateKanbanDto";
import { kanbanSliceSelectors } from "../../../redux/kanban/kanban.selector";
import { kanbanActions } from "../../../redux/kanban/kanban.slice";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { AtiraToast } from "../../../utils/AtiraToast";
import { onSetTypedCRUDErrors } from "../../../utils/onSetTypedCRUDErrors";
import {
  getTranslatedKanbanColumnName,
  getTranslatedKanbanName,
} from "../KanbanUtils";
import { useDrawersContext } from "../../tasks/drawers-context";
import { getTypedCRUDErrors } from "../../../utils/getTypedCRUDErros";
import { ExportKanbanCardsDto } from "../../../model/kanban/dto/ExportKanbanCardsDto";
import { AtiraSelect } from "../../../components/AtiraSelect";
import styled from "styled-components";
import { Rounded } from "../../../theme/Rounded";
import { Checkbox, Switch } from "../../../utils/local-antd";
import { Info } from "../../../components/Info";
import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";
import { faFile } from "@fortawesome/free-regular-svg-icons/faFile";

const StyledSelect = styled(AtiraSelect)`
  .ant-select {
    border: 1px solid
      ${({ theme, valid }) => (valid ? theme.transparent : theme.danger)} !important;
  }

  border-radius: ${Rounded.md};
`;

export const KanbanCardsExportDrawer: React.FC = () => {
  const [exportLoading, setExportLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const kanban = useAppSelector(kanbanSliceSelectors.selectUserCurrentKanban);
  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const kanbansMeta = useAppSelector(kanbanSliceSelectors.selectKanbansMeta);

  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { exportModalVisible, setExportModalVisible } = useDrawersContext();
  const { control, getValues, reset, setError, handleSubmit, watch } =
    useForm<ExportKanbanCardsDto>({
      defaultValues: {
        includeClosedCards: true,
        parentId: undefined,
        columnId: undefined,
        userId,
      },
    });
  const [selectedColumnId, parentId] = watch(["columnId", "parentId"]);

  const columnOptions = [
    {
      label: t("kanban.card.export.drawer.select.placeholder"),
      value: undefined,
    },
    ...(kanbansMeta
      ?.find(({ _id }) => _id === parentId)
      ?.columns?.filter((c) => !c.default)
      ?.map((c) => ({
        label: getTranslatedKanbanColumnName(c.name, kanban?.template),
        value: c._id,
      })) || []),
  ];

  const kanbanOptions = [
    {
      label: t("kanban.card.export.drawer.select.placeholder"),
      value: undefined,
    },
    ...kanbansMeta?.map((kanban) => ({
      label: getTranslatedKanbanName(kanban.name),
      value: kanban._id,
    })),
  ];

  const onSetErrors = (e: any) => {
    const errors = getTypedCRUDErrors(e);

    if (errors?.length) {
      setErrorMessage(errors[0].message);
    } else if (e.response?.data) {
      setErrorMessage(t(e.response.data));
    }
  };

  const onExport = async () => {
    try {
      setExportLoading(true);
      const { includeClosedCards, parentId, columnId } = getValues();

      // TODO this is just a big string, there might be a better way to handle this, but for now it works
      const response = await dispatch(
        kanbanActions.exportKanbanCards({
          userId,
          includeClosedCards,
          parentId,
          columnId,
        }),
      ).unwrap();

      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = url;

      const kanbanName = getTranslatedKanbanName(
        kanbansMeta?.find((kanban) => kanban._id === parentId)?.name ||
          "pipeline",
      );
      const columnName = getTranslatedKanbanColumnName(
        kanbansMeta
          ?.find((kanban) => kanban._id === parentId)
          ?.columns?.find((c) => c._id === selectedColumnId)?.name || "",
      );

      link.setAttribute(
        "download",
        `${kanbanName}${selectedColumnId ? `-${columnName}` : ""}-deals-${new Date().toISOString().split("T")[0]}.csv`,
      );

      document.body.appendChild(link);
      link.click();

      AtiraToast.success(t("entries.export.success"));

      link.parentNode?.removeChild(link);

      setExportModalVisible(false);
      setErrorMessage("");
      reset();
    } catch (e: any) {
      console.log(e);
      AtiraToast.apiError(e);
      onSetErrors(e);
    } finally {
      setExportLoading(false);
    }
  };

  //   useEffect(() => {
  //     if (kanban) {
  //       reset({
  //         name: kanbanName,
  //       });
  //     }
  //   }, [kanban, reset, kanbanName]);

  return (
    <Drawer
      open={exportModalVisible}
      onClose={() => setExportModalVisible(false)}
      title={t("kanban.card.export.drawer.title")}
      size="large"
    >
      <Flex flexDirection="column" gap="m">
        <Info info={t("kanban.card.export.drawer.info")} />
        <Info color="warning" info={t("kanban.card.export.drawer.info-2")} />

        <Controller
          control={control}
          name="parentId"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <StyledSelect
              title={t("kanban.card.export.drawer.parentId.select.title")}
              value={{
                label: !value
                  ? t("kanban.card.export.drawer.select.placeholder")
                  : getTranslatedKanbanName(
                      kanbansMeta?.find((kanban) => kanban._id === value)
                        ?.name || "",
                    ),
                value,
              }}
              onChange={onChange}
              options={kanbanOptions}
              disabled={exportLoading}
              valid={!error}
              errorMessage={error?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="columnId"
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <StyledSelect
              title={t("kanban.card.export.drawer.columnId.select.placeholder")}
              value={value}
              onChange={onChange}
              options={columnOptions}
              disabled={exportLoading || !parentId}
              valid={!error}
              errorMessage={error?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="includeClosedCards"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Flex gap="s" alignItems="center" marginBottom="s">
              <Text>
                {t("kanban.card.export.drawer.includeClosedDeals.switch.title")}
              </Text>

              <Switch
                checked={value}
                onChange={onChange}
                disabled={exportLoading}
              />
            </Flex>
          )}
        />

        <Button
          width="100%"
          onClick={handleSubmit(onExport)}
          loading={exportLoading}
          icon={faFile}
        >
          <Text color="white" fontSize="s">
            {t("common.export")}
          </Text>
        </Button>
      </Flex>
    </Drawer>
  );
};
